<script lang="ts">
    import DocumentHeader from '../shared/document-header.svelte'
    import DocumentFooter from '../shared/document-footer.svelte'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'

    /**
     * Moldura de impressão do documento: desenha o timbre (cabeçalho/rodapé configurados no modelo)
     * repetido em TODAS as páginas e reserva o espaço correspondente em cada uma.
     *
     * A mecânica são dois ingredientes que precisam andar juntos:
     *  - o `thead`/`tfoot` de uma tabela é repetido pelo navegador a cada página impressa — é o que
     *    reserva o espaço vertical do timbre;
     *  - um elemento `position: fixed` é repintado a cada página — é o que de fato desenha o timbre
     *    dentro daquele espaço.
     *
     * A altura da faixa é medida do conteúdo renderizado (`bind:clientHeight`), não fixada: o timbre
     * varia conforme tenha logo e quantas linhas de endereço/contato o modelo usa.
     *
     * O conteúdo do documento entra pelo slot e deve ser renderizado com `omitHeaderFooter`, senão o
     * timbre sairia duplicado na primeira página.
     */
    export let headerFooter: DocumentHeaderFooter
    /** Só necessário sem sessão autenticada (link público/lambda-pdf), para o cabeçalho que usa os
     * dados cadastrais da empresa conseguir buscá-los pela API pública. */
    export let companyId: string | null = null

    let headerHeight = 0
    let footerHeight = 0

    $: hasHeader = !!headerFooter?.header?.enabled
    $: hasFooter = !!(headerFooter?.footer?.enabled && headerFooter?.footer?.text)
</script>

<table class="fv2-frame">
    {#if hasHeader}
        <thead>
            <tr><td><div class="fv2-frame-space" style="--space-height: {headerHeight}px">&nbsp;</div></td></tr>
        </thead>
    {/if}
    <tbody>
        <tr><td><slot /></td></tr>
    </tbody>
    {#if hasFooter}
        <tfoot>
            <tr><td><div class="fv2-frame-space" style="--space-height: {footerHeight}px">&nbsp;</div></td></tr>
        </tfoot>
    {/if}
</table>

{#if hasHeader}
    <div class="fv2-frame-band fv2-frame-band-top" bind:clientHeight={headerHeight}>
        <DocumentHeader header={headerFooter.header} {companyId} />
    </div>
{/if}
{#if hasFooter}
    <div class="fv2-frame-band fv2-frame-band-bottom" bind:clientHeight={footerHeight}>
        <DocumentFooter footer={headerFooter.footer} />
    </div>
{/if}

<style>
    .fv2-frame {
        width: 100%;
        border-collapse: collapse;
    }
    .fv2-frame td {
        vertical-align: top;
        padding: 0;
    }
    .fv2-frame-space {
        /* `!important` é obrigatório: o css_print.css aplica `height: unset !important` em `body *`,
           que sem isso zera a altura do espaçador — o cabeçalho fixo passa a cobrir o texto. */
        height: var(--space-height, 0px) !important;
    }
    .fv2-frame-band {
        position: fixed;
        left: 0;
        right: 0;
        width: 100%;
        background-color: #fff;
        /* flow-root pra que a margem do card interno entre no clientHeight — sem isso a medição
           sairia menor que o espaço realmente ocupado e o conteúdo passaria por baixo do timbre. */
        display: flow-root;
    }
    .fv2-frame-band-top {
        top: 0;
    }
    .fv2-frame-band-bottom {
        bottom: 0;
    }
</style>
